/** Random-map templates.
 *
 * The classic's generator does not take a bag of numbers; it takes a *template* - a
 * named, hand-balanced shape with a size, a player count, a water share and a
 * monster strength, and a description that tells the player what kind of game
 * he is about to get. "Jebus Cross" and "Nostalgia" are not option presets,
 * they are the unit in which random maps are chosen and discussed.
 *
 * This build had `MapGenOptions` and nothing else, so every random map was the
 * same map at a different size and there was no way for a player to ask for a
 * *kind* of game. A template here is exactly a partial `MapGenOptions` plus the
 * two things a chooser needs - a name and a sentence - and nothing more: the
 * generator is not changed, and a template that produces a bad map is a data
 * edit rather than a code change.
 *
 * `content` is deliberately absent from `MapGenTemplate`. Everything else about
 * a map can be written down in advance; the registry cannot, and folding it in
 * would make a table of constants depend on a loaded bundle.
 */

import type { MapGenOptions } from "./generate.js";

/** The knobs a template is allowed to set: everything but the registry. */
export type TemplateOptions = Omit<MapGenOptions, "content">;

export interface MapGenTemplate {
  readonly id: string;
  readonly name: string;
  /** One sentence, in the words a player would use to choose between two maps. */
  readonly description: string;
  /** Player counts this template is balanced for; the chooser offers these. */
  readonly players: readonly number[];
  /** Cells on a side. A template is a shape, and a shape has a size. */
  readonly size: number;
  readonly options: TemplateOptions;
}

/**
 * The default shape, spelled out so every template below is a diff against
 * something readable rather than against the generator's own defaults.
 *
 * These are the generator's defaults as of this round: half a percent of cells
 * decorated, a fifth of a percent blocked by scenery, no water, monsters at
 * face value, an underground, one neutral town per two players, and zones cut
 * into chambers at four tenths of full strength.
 */
export const BASELINE: TemplateOptions = Object.freeze({
  waterBp: 0,
  monsterStrengthBp: 10000,
  decorationBp: 500,
  sceneryBp: 200,
  underground: true,
  mazeBp: 4000,
});

/**
 * A template that names no `waterShape` gets one drawn from the seed, which is
 * what `BASELINE` deliberately leaves out: "Crossroads" is a lake every time
 * because that is its character, and a template with no opinion should still
 * hand the player a different-looking board each time they reroll.
 */

const template = (
  id: string, name: string, description: string,
  size: number, players: readonly number[], options: Partial<TemplateOptions>,
): MapGenTemplate => ({
  id, name, description, size, players,
  options: { ...BASELINE, ...options },
});

/**
 * The shipped set.
 *
 * Six templates, chosen to span the axes that actually change how a game
 * plays - how far apart the starts are, how hard the ground between them
 * fights back, whether there is a second plane, and whether there is anything
 * on the board worth a detour - rather than to be a long list.
 */
export const TEMPLATES: readonly MapGenTemplate[] = Object.freeze([
  template(
    "duel", "Duel",
    "Two kingdoms, one small continent and nothing to hide behind. The shortest "
    + "game this generator makes.",
    48, [2],
    { underground: false, neutralTowns: 0, monsterStrengthBp: 8000, sceneryBp: 150,
      mazeBp: 1500 },
  ),
  template(
    "crossroads", "Crossroads",
    "The standard shape: room to expand, a handful of unowned towns in the middle, "
    + "and a cavern under all of it.",
    72, [2, 3, 4],
    { neutralTowns: 2, waterBp: 1500, waterShape: "lake" },
  ),
  template(
    "archipelago", "Archipelago",
    "Land broken up by open water. A hero without Navigation goes the long way "
    + "round, and the boat is worth more than the artifact.",
    72, [2, 3, 4],
    // Low: open water already breaks this board into pieces, and walling the
    // islands as well leaves nowhere to land.
    { waterBp: 3500, underground: false, neutralTowns: 2, sceneryBp: 120,
      mazeBp: 2000, waterShape: "islands" },
  ),
  template(
    "warrens", "The Warrens",
    "Half the map is underground and most of the neutral towns are down there. "
    + "Whoever holds the gates holds the map.",
    96, [2, 3, 4],
    { underground: true, neutralTowns: 4, sceneryBp: 320, decorationBp: 400,
      mazeBp: 7000 },
  ),
  template(
    "hard-country", "Hard Country",
    "The same board with everything on it fighting twice as hard. Expansion is "
    + "slow, and the first hundred days are spent earning it.",
    72, [2, 3, 4],
    { monsterStrengthBp: 20000, neutralTowns: 3, mazeBp: 5500, waterShape: "rivers" },
  ),
  template(
    "labyrinth", "The Labyrinth",
    "Mountain and forest cut the whole board into chambers with one way in and "
    + "one way out. Every march is a route, and the hero who knows the ground "
    + "arrives first.",
    72, [2, 3, 4],
    { neutralTowns: 2, mazeBp: 9500, sceneryBp: 120 },
  ),
  template(
    "thunder_colossi", "Thunder Colossi of the North",
    "The largest board this generator builds, and the only one where a kingdom "
    + "can grow for a month before it meets another. Frozen ranges and open "
    + "water between the crowns.",
    288, [4, 6, 8],
    { neutralTowns: 8, waterBp: 2200, mazeBp: 3500, monsterStrengthBp: 12000,
      waterShape: "coast" },
  ),
  template(
    "grand-melee", "Grand Melee",
    "Eight kingdoms on a very large board. Long, loud, and the one template where "
    + "a war can be fought on two fronts.",
    144, [4, 6, 8],
    { neutralTowns: 6, monsterStrengthBp: 12000, waterBp: 1200,
      waterShape: "patchwork" },
  ),
]);

export const DEFAULT_TEMPLATE_ID = "crossroads";

export function findTemplate(id: string): MapGenTemplate | null {
  for (const t of TEMPLATES) if (t.id === id) return t;
  return null;
}

/**
 * A template plus a player count, as the options `generateMap` takes.
 *
 * The player count is clamped into the template's own list rather than passed
 * through: a template is balanced for the counts it names, and "Duel with six
 * players" is not a map anybody asked for. The size travels with the template
 * for the same reason - a Grand Melee on a 48-cell board is eight capitals in a
 * cul-de-sac.
 */
export function templateOptions(
  t: MapGenTemplate, players: number,
): TemplateOptions & { readonly players: number; readonly width: number; readonly height: number } {
  return {
    ...t.options,
    players: nearestPlayerCount(t, players),
    width: t.size,
    height: t.size,
  };
}

export function nearestPlayerCount(t: MapGenTemplate, players: number): number {
  let best = t.players[0] ?? 2;
  let gap = Number.POSITIVE_INFINITY;
  for (const n of t.players) {
    const d = Math.abs(n - players);
    if (d < gap) { best = n; gap = d; }
  }
  return best;
}

/** Templates that can be played with this many kingdoms. */
export const templatesFor = (players: number): readonly MapGenTemplate[] =>
  TEMPLATES.filter((t) => t.players.includes(players));
